import { useState } from "react";
import {
  Holding,
  Cliente,
  Propiedad,
  Caso,
  CasoPropiedad,
  Contacto,
  Usuario,
  TipoTenencia,
} from "../../types";
import { Badge, getTipoClienteBadgeVariant, getEstadoCasoBadgeVariant } from "../common/Badge";
import { Modal } from "../common/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { CasoForm } from "../casos/CasoForm";
import { CambiarEstadoPropiedadModal } from "../common/CambiarEstadoPropiedadModal";

interface HoldingDetailProps {
  holding: Holding;
  clientes: Cliente[];
  propiedades: Propiedad[];
  casos: Caso[];
  casoPropiedades: CasoPropiedad[];
  contactos: Contacto[];
  usuarios: Usuario[];
  onClienteAssignToHolding: (clienteId: number, holdingId: number) => void;
  onClienteRemoveFromHolding: (clienteId: number) => void;
  onClienteDelete: (id: number) => void;
  onPropiedadCreate: (data: Omit<Propiedad, "id" | "fechaCreacion">) => void;
  onPropiedadUpdate: (propiedad: Propiedad) => void;
  onPropiedadDelete: (id: number) => void;
  onCasoCreate: (data: Omit<Caso, "id" | "fechaCreacion" | "fechaActualizacion">) => void;
  onCasoPropiedadCreate: (data: Omit<CasoPropiedad, "id">) => void;
  usuarioActual: Usuario;
}

export function HoldingDetail({
  holding,
  clientes,
  propiedades,
  casos,
  casoPropiedades,
  contactos,
  usuarios,
  onClienteAssignToHolding,
  onClienteRemoveFromHolding,
  onClienteDelete,
  onPropiedadCreate,
  onPropiedadUpdate,
  onPropiedadDelete,
  onCasoCreate,
  onCasoPropiedadCreate,
  usuarioActual,
}: HoldingDetailProps) {
  const [isNewCasoOpen, setIsNewCasoOpen] = useState(false);
  const [isAddClienteOpen, setIsAddClienteOpen] = useState(false);
  const [selectedClienteToAdd, setSelectedClienteToAdd] = useState<number | "">("");
  const [clienteToDelete, setClienteToDelete] = useState<Cliente | null>(null);
  const [clienteToRemove, setClienteToRemove] = useState<Cliente | null>(null);
  const [propToDelete, setPropToDelete] = useState<Propiedad | null>(null);
  const [propToChangeEstado, setPropToChangeEstado] = useState<Propiedad | null>(null);
  const isAdmin = usuarioActual.rol === "admin";

  // Inline propiedad form state
  const [addingPropForClienteId, setAddingPropForClienteId] = useState<number | null>(null);
  const [newPropComuna, setNewPropComuna] = useState("");
  const [newPropRol, setNewPropRol] = useState("");
  const [newPropTipo, setNewPropTipo] = useState<TipoTenencia>("propia");
  const [newPropRutLeasing, setNewPropRutLeasing] = useState("");

  const holdingClientes = clientes.filter((c) => c.holdingId === holding.id);
  const holdingClienteIds = holdingClientes.map((c) => c.id);
  const holdingPropiedades = propiedades.filter((p) => holdingClienteIds.includes(p.clienteId));
  const holdingCasos = casos.filter((c) => c.holdingId === holding.id);
  const casosNoDesistidos = holdingCasos.filter((c) => c.estado !== "desistido");
  const casosDesistidos = holdingCasos.filter((c) => c.estado === "desistido");
  const casosActivos = holdingCasos.filter(
    (c) => !["cobro_facturacion", "desistido"].includes(c.estado)
  );
  const casosGanados = holdingCasos.filter((c) => c.resultado === "ganado").length;
  const casosPerdidos = holdingCasos.filter((c) => c.resultado === "perdido").length;
  const facturacionTotal = holdingCasos
    .filter((c) => c.resultado === "ganado")
    .reduce((sum, c) => sum + (c.honorario || 0), 0);
  const holdingContactos = contactos.filter((c) => holdingClienteIds.includes(c.clienteId));

  const formatMoney = (value: number) => `$${value.toLocaleString("es-CL")}`;

  const handleCasoSubmit = (data: Omit<Caso, "id" | "fechaCreacion" | "fechaActualizacion">) => {
    onCasoCreate(data);
    setIsNewCasoOpen(false);
  };

  // Get clients that are not assigned to any holding
  const availableClientes = clientes.filter((c) => !c.holdingId);

  const handleAddClienteToHolding = () => {
    if (selectedClienteToAdd && typeof selectedClienteToAdd === "number") {
      onClienteAssignToHolding(selectedClienteToAdd, holding.id);
      setSelectedClienteToAdd("");
      setIsAddClienteOpen(false);
    }
  };

  const resetPropForm = () => {
    setAddingPropForClienteId(null);
    setNewPropComuna("");
    setNewPropRol("");
    setNewPropTipo("propia");
    setNewPropRutLeasing("");
  };

  const handlePropSubmit = () => {
    if (!addingPropForClienteId || !newPropComuna.trim() || !newPropRol.trim()) return;
    onPropiedadCreate({
      clienteId: addingPropForClienteId,
      comuna: newPropComuna.trim(),
      rol: newPropRol.trim(),
      tipoTenencia: newPropTipo,
      rutLeasing: newPropTipo === "leasing" ? newPropRutLeasing.trim() || undefined : undefined,
      estadoPropiedad: "activa",
    });
    resetPropForm();
  };

  const getTipoClienteLabel = (tipo: string) => {
    switch (tipo) {
      case "holding": return "Holding";
      case "persona_natural": return "Persona Natural";
      default: return tipo;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-xl font-semibold text-slate-800">{holding.nombre}</h2>
          <Badge variant={getTipoClienteBadgeVariant(holding.tipo)}>{holding.tipo}</Badge>
          <Badge variant={holding.activo ? "success" : "neutral"}>
            {holding.activo ? "Activo" : "Inactivo"}
          </Badge>
        </div>
        {holding.descripcion && <p className="text-slate-600">{holding.descripcion}</p>}
        <p className="text-sm text-slate-500 mt-2">
          Creado el {new Date(holding.fechaCreacion).toLocaleDateString("es-CL")}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-600">Propiedades</p>
          <p className="text-2xl font-bold text-blue-800">{holdingPropiedades.length}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-600">Casos Total</p>
          <p className="text-2xl font-bold text-slate-800">{holdingCasos.length}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-3">
          <p className="text-xs text-yellow-600">Casos Activos</p>
          <p className="text-2xl font-bold text-yellow-800">{casosActivos.length}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs text-green-600">Ganados</p>
          <p className="text-2xl font-bold text-green-800">{casosGanados}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3">
          <p className="text-xs text-red-600">Perdidos</p>
          <p className="text-2xl font-bold text-red-800">{casosPerdidos}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3">
          <p className="text-xs text-emerald-600">Facturacion</p>
          <p className="text-lg font-bold text-emerald-800">
            {facturacionTotal > 0 ? formatMoney(facturacionTotal) : "$0"}
          </p>
        </div>
      </div>

      {/* Contactos */}
      {holdingContactos.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Contactos ({holdingContactos.length})
          </h3>
          <div className="space-y-1">
            {holdingContactos.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-sm text-slate-600 bg-white rounded p-2 border border-slate-100">
                <span className="font-medium">{c.nombre}</span>
                <span className="text-slate-400">-</span>
                <span>{c.email}</span>
                {c.telefono && <span className="text-slate-400">| {c.telefono}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clientes Asociados con Propiedades */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Clientes ({holdingClientes.length})
          </h3>
          <button
            onClick={() => setIsAddClienteOpen(true)}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
            disabled={availableClientes.length === 0}
            title={availableClientes.length === 0 ? "No hay clientes disponibles para agregar" : "Agregar cliente existente"}
          >
            + Agregar clientes
          </button>
        </div>
        {holdingClientes.length > 0 ? (
          <div className="space-y-3">
            {holdingClientes.map((cliente) => {
              const clienteProps = propiedades.filter((p) => p.clienteId === cliente.id);
              const isAddingProp = addingPropForClienteId === cliente.id;
              return (
                <div key={cliente.id} className="p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-800">{cliente.nombre}</p>
                        <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                          {getTipoClienteLabel(cliente.tipoCliente)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">{cliente.rut}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={cliente.estado === "activo" ? "success" : "neutral"}>
                        {cliente.estado}
                      </Badge>
                      <button
                        onClick={() => setClienteToRemove(cliente)}
                        className="p-1 text-slate-400 hover:text-amber-600 transition-colors"
                        title="Quitar del holding"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setClienteToDelete(cliente)}
                        className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                        title="Eliminar cliente"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Propiedades del Cliente */}
                  <div className="mt-2">
                    {clienteProps.length > 0 && (
                      <div className="pl-2 border-l-2 border-slate-200 space-y-1">
                        {clienteProps.map((p) => (
                          <div key={p.id} className={`flex items-center justify-between group py-0.5 ${
                            p.estadoPropiedad !== "activa" ? "opacity-70" : ""
                          }`}>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs text-slate-600">
                                <span className="font-medium">ROL {p.rol}</span> - {p.comuna}
                                <span className="ml-1.5 text-slate-400">
                                  ({p.tipoTenencia === "concesion" ? "Concesion" : p.tipoTenencia === "leasing" ? `Leasing${p.rutLeasing ? ` - RUT: ${p.rutLeasing}` : ""}` : "Propia"})
                                </span>
                              </p>
                              {p.estadoPropiedad !== "activa" && (
                                <Badge variant={p.estadoPropiedad === "desvinculada" ? "warning" : "error"}>
                                  {p.estadoPropiedad}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-0.5">
                              {isAdmin && (
                                <button
                                  onClick={() => setPropToChangeEstado(p)}
                                  className="p-0.5 text-slate-300 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-all"
                                  title="Cambiar estado"
                                >
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </button>
                              )}
                              <button
                                onClick={() => setPropToDelete(p)}
                                className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                title="Eliminar propiedad"
                              >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Inline add propiedad form */}
                    {isAddingProp ? (
                      <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={newPropComuna}
                            onChange={(e) => setNewPropComuna(e.target.value)}
                            placeholder="Comuna"
                            className="px-2 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            autoFocus
                          />
                          <input
                            type="text"
                            value={newPropRol}
                            onChange={(e) => setNewPropRol(e.target.value)}
                            placeholder="ROL (ej: 1234-56)"
                            className="px-2 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <select
                            value={newPropTipo}
                            onChange={(e) => setNewPropTipo(e.target.value as TipoTenencia)}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="propia">Propia</option>
                            <option value="leasing">Leasing</option>
                            <option value="concesion">Concesion</option>
                          </select>
                        </div>
                        {newPropTipo === "leasing" && (
                          <input
                            type="text"
                            value={newPropRutLeasing}
                            onChange={(e) => setNewPropRutLeasing(e.target.value)}
                            placeholder="RUT Leasing (ej: 76.999.888-7)"
                            className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        )}
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={resetPropForm}
                            className="px-2 py-1 text-xs text-slate-600 hover:text-slate-800"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={handlePropSubmit}
                            disabled={!newPropComuna.trim() || !newPropRol.trim()}
                            className="px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-slate-300"
                          >
                            Agregar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setAddingPropForClienteId(cliente.id)}
                        className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                      >
                        + Agregar Propiedad
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">No hay clientes asociados</p>
        )}
      </div>

      {/* Casos (no desistidos) */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Casos ({casosNoDesistidos.length})</h3>
          <button
            onClick={() => setIsNewCasoOpen(true)}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Crear Caso
          </button>
        </div>
        {casosNoDesistidos.length > 0 ? (
          <div className="space-y-2">
            {casosNoDesistidos.map((caso) => (
              <div key={caso.id} className="p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-slate-800">{caso.titulo}</span>
                  <Badge variant={getEstadoCasoBadgeVariant(caso.estado)}>
                    {caso.estado.replace("_", " ")}
                  </Badge>
                  {caso.resultado && (
                    <Badge variant={caso.resultado === "ganado" ? "success" : "error"}>
                      {caso.resultado}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-3 mt-1 text-xs text-slate-500">
                  <span>{caso.tipoPresentacion}</span>
                  {caso.honorario && <span>Honorario: {formatMoney(caso.honorario)}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4 bg-white rounded-lg border border-slate-200">
            No hay casos
          </p>
        )}
      </div>

      {/* Desistidos */}
      {casosDesistidos.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-slate-700 text-red-600">
              Desistidos ({casosDesistidos.length})
            </h3>
          </div>
          <div className="space-y-2">
            {casosDesistidos.map((caso) => (
              <div key={caso.id} className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-slate-800">{caso.titulo}</span>
                  <Badge variant="error">
                    desistido
                  </Badge>
                </div>
                <div className="flex gap-3 mt-1 text-xs text-slate-500">
                  <span>{caso.tipoPresentacion}</span>
                  {caso.honorario && <span>Honorario: {formatMoney(caso.honorario)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Agregar Cliente */}
      <Modal
        isOpen={isAddClienteOpen}
        onClose={() => { setIsAddClienteOpen(false); setSelectedClienteToAdd(""); }}
        title="Agregar clientes"
      >
        <div className="space-y-4">
          {availableClientes.length > 0 ? (
            <>
              <p className="text-sm text-slate-600">
                Selecciona un cliente para agregarlo a este holding:
              </p>
              <select
                value={selectedClienteToAdd}
                onChange={(e) => setSelectedClienteToAdd(e.target.value ? Number(e.target.value) : "")}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccionar cliente...</option>
                {availableClientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre} ({c.rut})
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => { setIsAddClienteOpen(false); setSelectedClienteToAdd(""); }}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAddClienteToHolding}
                  disabled={!selectedClienteToAdd}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  Guardar
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-slate-500">
                No hay clientes disponibles para agregar.
              </p>
              <p className="text-xs text-slate-400 mt-2">
                Primero crea clientes en la seccion "Clientes".
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Nuevo Caso */}
      <Modal
        isOpen={isNewCasoOpen}
        onClose={() => setIsNewCasoOpen(false)}
        title="Nuevo Caso"
        size="lg"
      >
        <CasoForm
          holdings={[holding]}
          propiedades={holdingPropiedades}
          clientes={holdingClientes}
          usuarios={usuarios}
          defaultHoldingId={holding.id}
          onSubmit={handleCasoSubmit}
          onCancel={() => setIsNewCasoOpen(false)}
        />
      </Modal>

      {/* Confirm eliminar Cliente */}
      <ConfirmDialog
        isOpen={clienteToDelete !== null}
        onClose={() => setClienteToDelete(null)}
        onConfirm={() => {
          if (clienteToDelete) onClienteDelete(clienteToDelete.id);
          setClienteToDelete(null);
        }}
        title="Eliminar Cliente"
        message={`Eliminar "${clienteToDelete?.nombre}" y todas sus propiedades asociadas?`}
        confirmText="Eliminar"
      />

      {/* Confirm quitar del holding */}
      <ConfirmDialog
        isOpen={clienteToRemove !== null}
        onClose={() => setClienteToRemove(null)}
        onConfirm={() => {
          if (clienteToRemove) onClienteRemoveFromHolding(clienteToRemove.id);
          setClienteToRemove(null);
        }}
        title="Quitar del Holding"
        message={`Quitar "${clienteToRemove?.nombre}" de este holding? El cliente seguira existiendo pero sin asociacion a ningun holding.`}
        confirmText="Quitar"
        variant="warning"
      />

      {/* Confirm eliminar Propiedad */}
      <ConfirmDialog
        isOpen={propToDelete !== null}
        onClose={() => setPropToDelete(null)}
        onConfirm={() => {
          if (propToDelete) onPropiedadDelete(propToDelete.id);
          setPropToDelete(null);
        }}
        title="Eliminar Propiedad"
        message={`Eliminar ROL ${propToDelete?.rol} - ${propToDelete?.comuna}?`}
        confirmText="Eliminar"
      />

      {/* Modal Cambiar Estado Propiedad */}
      <Modal
        isOpen={propToChangeEstado !== null}
        onClose={() => setPropToChangeEstado(null)}
        title="Cambiar Estado de ROL"
      >
        {propToChangeEstado && (
          <CambiarEstadoPropiedadModal
            propiedad={propToChangeEstado}
            onSubmit={(updated) => {
              onPropiedadUpdate(updated);
              setPropToChangeEstado(null);
            }}
            onCancel={() => setPropToChangeEstado(null)}
          />
        )}
      </Modal>
    </div>
  );
}
